import styled from "@emotion/styled"
import { PButton } from "@porsche-design-system/components-react"
import { breakpoint, spacingStaticMd } from "@porsche-design-system/components-react/emotion"
import { useEffect, useState } from "react"
import { FormProvider } from "react-hook-form"
import { FormattedMessage, useIntl } from "react-intl"
import { useNavigate, useParams } from "react-router"
import {
	AppTitle,
	BackNavigation,
	BackNavigationWrapper,
	ButtonGroup,
	ContainerHeader,
	ContentWrapper,
	Main,
	Skeleton,
	Stage,
	StageList,
} from "src/components"
import { trackPageView } from "src/components/adobeAnalytics"
import { useModal } from "src/context/modal/useModal"
import { useDateSettingsOutsideDealership } from "src/hooks"
import { NewsStatusTag } from "../newsList/components/NewsStatusTag"
import { newsListPath } from "../newsList/newsListPath"
import { NewsEditorForm } from "./components/NewsEditorForm"
import { NewsEditorFormSkeleton } from "./components/NewsEditorFormSkeleton"
import { NewsLanguageCoverageList } from "./components/NewsLanguageCoverageList"
import { NewsPreview } from "./components/NewsPreview"
import { NewsPreviewSkeleton } from "./components/NewsPreviewSkeleton"
import { NewsTransitionModal } from "./components/NewsTransitionModal"
import { newsEditorMessages as messages } from "./components/newsEditor.messages"
import { useNewsEditor } from "./hooks/useNewsEditor/useNewsEditor"

export const NewsEditorContainer = () => {
	const { newsItemId } = useParams()
	const navigate = useNavigate()
	const { formatMessage } = useIntl()
	const showTransitionModal = useModal(NewsTransitionModal)
	const isEdit = !!newsItemId
	const [heroImageUrl, setHeroImageUrl] = useState<string>()

	useEffect(() => trackPageView(isEdit ? "news_edit" : "news_create"), [isEdit])

	const goToList = () => navigate(newsListPath)
	const {
		form,
		images,
		isLoading,
		isUploadingImages,
		languageCoverage,
		onPublish,
		onSave,
		onSendToReview,
		savingAction,
		setIsUploadingImages,
		status,
	} = useNewsEditor({
		newsItemId,
		onSaved: goToList,
	})

	const { dateFormat, locale } = useDateSettingsOutsideDealership()

	const title = formatMessage(newsItemId ? messages.pageTitleEdit : messages.pageTitleNew)
	const isReadOnly = status !== undefined && status !== "DRAFT"

	// validates before asking for the confirmation, so the confirmation is never followed by a validation error
	const onSendToReviewClick = form.handleSubmit(() =>
		showTransitionModal({
			confirmLabel: formatMessage(messages.sendToReview),
			heading: formatMessage(messages.sendToReviewModalTitle),
			message: formatMessage(messages.sendToReviewModalMessage, { title: form.getValues("title") }),
			onConfirm: onSendToReview,
		}),
	)

	const onPublishClick = () =>
		showTransitionModal({
			confirmLabel: formatMessage(messages.publish),
			heading: formatMessage(messages.publishModalTitle),
			message: formatMessage(messages.publishModalMessage, { title: form.getValues("title") }),
			onConfirm: onPublish,
		})

	return (
		<Main>
			<BackNavigationWrapper>
				<BackNavigation backUrl={newsListPath} />
			</BackNavigationWrapper>
			<ContentWrapper>
				<AppTitle title={title} locationCode={undefined} />
				<Header
					title={title}
					isBelowBackNavigation
					belowTitle={status ? <NewsStatusTag status={status} /> : isLoading ? <Skeleton.Tag /> : undefined}
				/>
			</ContentWrapper>
			<StageList>
				<FormProvider {...form}>
					<Stages>
						<Stage>
							{isLoading ? (
								<NewsEditorFormSkeleton />
							) : (
								<>
									<NewsEditorForm
										dateFormat={dateFormat}
										images={images}
										isReadOnly={isReadOnly}
										locale={locale}
										onFirstImageUrlChange={setHeroImageUrl}
										onUploadingImagesChange={setIsUploadingImages}
									/>
									{languageCoverage.length > 0 && (
										<NewsLanguageCoverageList languageCoverage={languageCoverage} />
									)}
									<ButtonGroup>
										{isReadOnly ? (
											<>
												{status === "REVIEW" && (
													<PButton
														disabled={isUploadingImages}
														loading={savingAction === "publish"}
														onClick={onPublishClick}
													>
														<FormattedMessage {...messages.publish} />
													</PButton>
												)}
												<PButton type="button" variant="secondary" onClick={goToList}>
													<FormattedMessage {...messages.backToOverview} />
												</PButton>
											</>
										) : (
											<>
												<PButton
													disabled={isUploadingImages || savingAction === "save"}
													loading={savingAction === "sendToReview"}
													onClick={onSendToReviewClick}
												>
													<FormattedMessage {...messages.sendToReview} />
												</PButton>
												<PButton
													disabled={isUploadingImages || savingAction === "sendToReview"}
													loading={savingAction === "save"}
													variant="secondary"
													onClick={onSave}
												>
													<FormattedMessage {...messages.saveAsDraft} />
												</PButton>
												<PButton type="button" variant="secondary" onClick={goToList}>
													<FormattedMessage {...messages.discard} />
												</PButton>
											</>
										)}
									</ButtonGroup>
								</>
							)}
						</Stage>
						<Stage>
							{isLoading ? <NewsPreviewSkeleton /> : <NewsPreview heroImageUrl={heroImageUrl} />}
						</Stage>
					</Stages>
				</FormProvider>
			</StageList>
		</Main>
	)
}

const Header = styled(ContainerHeader)`
	padding-bottom: ${spacingStaticMd};
`

const Stages = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr);
	gap: ${spacingStaticMd};
	align-items: start;

	@media (min-width: ${breakpoint.l}px) {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
`
